import React, { useEffect, useState } from "react";
import { useGetSentimentsBreakdownMutation } from "../../../store/api/lumi/sentiment";
import { getConceptFilter } from "../../../utils/getConceptFilter";
import { debounce } from "lodash";
import { Spin, Divider } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import classes from "./MetadataSentimentBreakdown.module.css";
import Scrollbars from "react-custom-scrollbars-2";
import { FilterNameSelector } from "../../DashboardComponents/DashboardFilters/shared";
import SmallMultipleSentimentChart from "./SmallMultipleSentimentChart";
import LabelSelector from "./LabelSelector";

const MetadataSentimentBreakdown = (props) => {
  const { filters, metaDataQu, breakdownName, setBreakdownName, match_type } = props;
  const metaData = metaDataQu?.metaDataWithStringType || {};
  const [getSentimentsBreakdown] = useGetSentimentsBreakdownMutation();
  const [loading, setLoading] = useState(false);
  const [processedData, setProcessedData] = useState([]);

  // Default toggled labels: show Positive, Neutral, and Negative by default.
  const [selectedLabels, setSelectedLabels] = useState(["Positive", "Neutral", "Negative"]);
  const toggleLabel = (label) => {
    if (selectedLabels.includes(label)) {
      setSelectedLabels(selectedLabels.filter((l) => l !== label));
    } else {
      setSelectedLabels([...selectedLabels, label]);
    }
  };

  async function getSentimentData() {
    setLoading(true);
    try {
      const conceptFilter = getConceptFilter(props);
      const { data } = await getSentimentsBreakdown({
        id: props.id,
        concept_selector: conceptFilter,
        filter: props.filters,
        match_type,
        breakdowns: [
          { name: Array.isArray(breakdownName) ? breakdownName[0] : breakdownName }
        ],
        include_examples: true,
      });

      console.log("Sentiment Breakdown API response:", data);
      props.setFilterDocCount(data.filter_count);

      if (!data?.breakdowns?.[0]?.buckets) {
        console.error("Unexpected API response structure:", data);
        setProcessedData([]);
        setLoading(false);
        return;
      }

      const buckets = data.breakdowns[0].buckets;
      // Aggregate counts for each bucket
      const aggregatedData = buckets
        .filter((bucket) => {
          if (!bucket.match_counts || bucket.match_counts.length === 0) return false;
          const bucketTotal = bucket.match_counts.reduce(
            (sum, m) => sum + (m.sentiment_counts ? m.sentiment_counts.total : 0),
            0
          );
          return bucketTotal > 0;
        })
        .map((bucket) => {
          let totalPos = 0, totalNeutral = 0, totalNeg = 0;
          bucket.match_counts.forEach((m) => {
            if (m.sentiment_counts) {
              totalPos += m.sentiment_counts.positive || 0;
              totalNeutral += m.sentiment_counts.neutral || 0;
              totalNeg += m.sentiment_counts.negative || 0;
            }
          });
          const chartData = [
            {
              sentiment: "Sentiment",
              positive: totalPos,
              neutral: totalNeutral,
              negative: totalNeg,
            },
          ];
          return {
            location: bucket.label,
            chartData,
          };
        })
        // Sort buckets by negative count descending
        .sort((a, b) => b.chartData[0].negative - a.chartData[0].negative)
        .slice(0, 100);

      setProcessedData(aggregatedData);
    } catch (error) {
      console.error("Error fetching sentiment breakdown:", error);
      setProcessedData([]);
    }
    setLoading(false);
  }

  useEffect(() => {
    const debouncedGet = debounce(getSentimentData, 500);
    debouncedGet();
    return () => {
      debouncedGet.cancel();
    };
  }, [filters, breakdownName, props.conceptTypeDay, props.selectedLocalConcepts, props.conceptList, match_type]);

  useEffect(() => {
    props.setAdditionalFilters([
      {
        key: "Special",
        label: "Select Breakdown Variable:",
        showArrow: false,
        children: (
          <FilterNameSelector
            selectedFieldName={breakdownName}
            setSelectedFieldName={setBreakdownName}
            metaData={metaData}
          />
        ),
      },
    ]);
  }, [breakdownName, metaData]);

  if (loading) {
    return (
      <Spin className="spinner-by-center" indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
    );
  }

  // Compute a global maximum for Y axis based on the total sentiment count in each bucket
  const globalMax = processedData.reduce((max, group) => {
    const groupTotal = group.chartData[0].positive + group.chartData[0].neutral + group.chartData[0].negative;
    return groupTotal > max ? groupTotal : max;
  }, 0);

  return (
    <div className={classes.widgetContainer} style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Top label selector */}
      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "8px", padding: "8px" }}>
        <LabelSelector
          labels={["Positive", "Neutral", "Negative"]}
          selectedLabels={selectedLabels}
          onToggleLabel={toggleLabel}
        />
      </div>
      {/* Scrollable area: grid of sentiment charts */}
      <div style={{ flex: 1 }}>
        <Scrollbars style={{ height: "100%" }}>
          {processedData.length > 0 ? (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                gap: "0.5rem",
                padding: "0.5rem",
              }}
            >
              {processedData.map((group, index) => (
                <SmallMultipleSentimentChart
                  key={`group-${index}`}
                  label={group.location}
                  data={group.chartData}
                  showPositive={selectedLabels.includes("Positive")}
                  showNeutral={selectedLabels.includes("Neutral")}
                  showNegative={selectedLabels.includes("Negative")}
                  maxYAxis={globalMax}
                />
              ))}
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: "1rem" }}>No data available</div>
          )}
        </Scrollbars>
      </div>
    </div>
  );
};

export default MetadataSentimentBreakdown;
